import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Plus, PencilSimple, Trash, Check, Tag } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable modal to manage categories for the article library or the
 * materials catalog. Lists / adds / renames / deletes (with optional
 * reassignment of items to another category on delete).
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - kind: 'articles' | 'materiaux'
 *  - onChange: () => void  // called after any mutation so the parent can refetch
 */
export const CategoryManagerModal = ({ open, onClose, kind, onChange }) => {
  const isArticles = kind === 'articles';
  const baseUrl = isArticles
    ? `${API_URL}/api/invoicing/library/categories`
    : `${API_URL}/api/materials/categories`;

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingKey, setEditingKey] = useState(null); // articles: id, materiaux: name
  const [editValue, setEditValue] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null); // {key, name}
  const [migrateTo, setMigrateTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(baseUrl, { withCredentials: true });
      setCategories(r.data || []);
    } catch {
      toast.error('Erreur chargement catégories');
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  useEffect(() => {
    if (open) {
      load();
      setNewName('');
      setEditingKey(null);
      setConfirmDelete(null);
      setMigrateTo('');
    }
  }, [open, load]);

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await axios.post(baseUrl, { name }, { withCredentials: true });
      toast.success('Catégorie ajoutée');
      setNewName('');
      await load();
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur ajout');
    }
  };

  const handleRename = async (cat) => {
    const name = editValue.trim();
    if (!name) {
      toast.error('Nom requis');
      return;
    }
    try {
      if (isArticles) {
        await axios.put(`${baseUrl}/${cat.id}`, { name }, { withCredentials: true });
      } else {
        await axios.put(`${baseUrl}/${encodeURIComponent(cat.name)}`, { name }, { withCredentials: true });
      }
      toast.success('Catégorie renommée');
      setEditingKey(null);
      setEditValue('');
      await load();
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur renommage');
    }
  };

  const openDelete = (cat) => {
    setConfirmDelete(cat);
    setMigrateTo('');
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    const target = migrateTo === '__none__' ? '' : migrateTo;
    try {
      const url = isArticles
        ? `${baseUrl}/${confirmDelete.id}`
        : `${baseUrl}/${encodeURIComponent(confirmDelete.name)}`;
      await axios.delete(url, {
        params: target ? { migrate_to: target } : {},
        withCredentials: true,
      });
      toast.success('Catégorie supprimée');
      setConfirmDelete(null);
      setMigrateTo('');
      await load();
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur suppression');
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid={`category-manager-modal-${kind}`}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, padding: 20 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background: 'white', borderRadius: 10, width: '100%', maxWidth: 560, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E2E2E6' }}>
          <h3 style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: '1.05rem', fontWeight: 700 }}>
            <Tag size={18} weight="bold" color="#002FA7" />
            Gérer les catégories — {isArticles ? 'Articles' : 'Matériaux'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5E5F66' }} data-testid={`category-manager-close-${kind}`}>
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: '16px 20px' }}>
          {/* Add new */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <input
              data-testid={`category-new-input-${kind}`}
              className="form-input"
              placeholder="Nouvelle catégorie..."
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              style={{ flex: 1 }}
            />
            <button
              className="btn btn-primary"
              onClick={handleAdd}
              disabled={!newName.trim()}
              data-testid={`category-add-btn-${kind}`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <Plus size={14} weight="bold" /> Ajouter
            </button>
          </div>

          {/* List */}
          {loading ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#5E5F66' }}>Chargement…</div>
          ) : categories.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#9CA3AF', fontStyle: 'italic' }}>
              Aucune catégorie. Ajoutez-en une ci-dessus.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: '50vh', overflow: 'auto' }}>
              {categories.map((c) => {
                const key = isArticles ? c.id : c.name;
                const isEditing = editingKey === key;
                return (
                  <div
                    key={key}
                    data-testid={`category-row-${kind}-${key}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '8px 10px', borderRadius: 6,
                      background: isEditing ? '#EFF6FF' : '#F8F9FB',
                      border: '1px solid #E5E7EB',
                    }}
                  >
                    {isArticles && c.number != null && (
                      <span style={{ width: 30, textAlign: 'center', fontSize: '0.75rem', fontWeight: 700, color: '#5E5F66', background: 'white', border: '1px solid #E5E7EB', padding: '2px 4px', borderRadius: 4 }}>
                        {c.number}
                      </span>
                    )}
                    {isEditing ? (
                      <input
                        autoFocus
                        className="form-input"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleRename(c);
                          if (e.key === 'Escape') { setEditingKey(null); setEditValue(''); }
                        }}
                        data-testid={`category-edit-input-${kind}-${key}`}
                        style={{ flex: 1, fontSize: '0.9rem' }}
                      />
                    ) : (
                      <span style={{ flex: 1, fontSize: '0.92rem', fontWeight: 500, color: '#1F2937' }}>{c.name}</span>
                    )}
                    {isEditing ? (
                      <>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleRename(c)}
                          data-testid={`category-save-${kind}-${key}`}
                          title="Enregistrer"
                        >
                          <Check size={14} weight="bold" />
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => { setEditingKey(null); setEditValue(''); }}
                          data-testid={`category-cancel-${kind}-${key}`}
                          title="Annuler"
                        >
                          <X size={14} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => { setEditingKey(key); setEditValue(c.name); }}
                          data-testid={`category-rename-${kind}-${key}`}
                          title="Renommer"
                        >
                          <PencilSimple size={14} />
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => openDelete(c)}
                          data-testid={`category-delete-${kind}-${key}`}
                          title="Supprimer"
                        >
                          <Trash size={14} />
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Delete confirm with migration target */}
      {confirmDelete && (
        <div
          data-testid={`category-delete-confirm-${kind}`}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: 20 }}
          onClick={(e) => e.target === e.currentTarget && setConfirmDelete(null)}
        >
          <div style={{ background: 'white', borderRadius: 10, width: '100%', maxWidth: 460, padding: 22 }}>
            <h4 style={{ margin: '0 0 10px 0', fontSize: '1rem', fontWeight: 700, color: '#1F2937' }}>
              Supprimer la catégorie « {confirmDelete.name} »
            </h4>
            <p style={{ margin: '0 0 14px 0', fontSize: '0.85rem', color: '#5E5F66', lineHeight: 1.5 }}>
              Vers quelle catégorie souhaitez-vous déplacer les {isArticles ? 'articles' : 'matériaux'} liés ?
              Choisissez « Aucune » pour les laisser sans catégorie.
            </p>
            <Select value={migrateTo} onValueChange={setMigrateTo}>
              <SelectTrigger data-testid={`category-migrate-select-${kind}`}>
                <SelectValue placeholder="Choisir une catégorie cible..." />
              </SelectTrigger>
              <SelectContent className="z-[9999]">
                <SelectItem value="__none__">— Aucune (laisser vide) —</SelectItem>
                {categories
                  .filter((c) => (isArticles ? c.id : c.name) !== (isArticles ? confirmDelete.id : confirmDelete.name))
                  .map((c) => (
                    <SelectItem key={isArticles ? c.id : c.name} value={c.name}>
                      {c.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
              <button className="btn btn-secondary" onClick={() => setConfirmDelete(null)} data-testid={`category-delete-cancel-${kind}`}>
                Annuler
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={!migrateTo}
                data-testid={`category-delete-confirm-btn-${kind}`}
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryManagerModal;
